import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { companyNameQuery } from '../../queries';
import {
  setAdriverSelectedCity,
  setCurrentState,
  toggleAddNewItemModal
} from '../../actions';
import LinkWithIcon from '../LinkWithIcon';
import Tooltip from '../Tooltip';
import LoginButton from '../LoginButton';
import HamburgerMenu from '../HamburgerMenu';
import Icon from '../Icon';
import WhitePlusInCircle from '../../public/static/images/icon-plus-in-circle--white.svg';
import Plus from '../../public/static/images/green-plus.svg';
import CloseWebview from '../../public/static/images/close-webview.svg';
import useScreenSize from '../../hooks/useScreenSize';
import useBugsnagNotifier from '../../hooks/useBugsnagNotifier';
import { useNewItemLocalState } from '../../hooks/useNewItemLocalState';
import { useBridgeAPI, useBridgeState } from '../../bridge';
import { getIsCurrentPage } from '../../utils/helpers/getIsCurrentPage';
import {
  ButtonsGroupContainer,
  FlexedHeader,
  MenuItem,
  MenuItemsContainer,
  BookmarkIconLink,
  WebViewButton,
  Wrapper,
  CreateItemIconLink
} from './styled';
import {
  Cities,
  BUILDING_TYPE_ID,
  SEARCH_RESULT_ROUTES
} from '../../utils/helpers/constants';
import { selectSearchFormCurrentState } from '../../store/selectors';

export interface FlexibleHeaderProps {
  isShowCloseButton: boolean;
  isFixedHeader: boolean;
}

export const FlexibleHeader = ({
  isShowCloseButton,
  isFixedHeader
}: FlexibleHeaderProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const storedFormState = useSelector(selectSearchFormCurrentState);
  const { isWebView } = useBridgeState();
  const { closeWebview } = useBridgeAPI();
  const { isSmall, isMedium, isLarge, isSmallDesktop } = useScreenSize();
  const { setFirstViewValue } = useNewItemLocalState();
  const {
    data,
    error,
    loading: isLoadingProfile
  } = useQuery(companyNameQuery, {
    ssr: false
  });
  const isAuth = Boolean(data && data?.profile);
  useBugsnagNotifier(error);

  const onMobileIconPlusClick = () => {
    dispatch(toggleAddNewItemModal(true));
    setFirstViewValue('false');
  };

  const menuItemsList = [
    {
      title: t('main_menu.sale'),
      path: SEARCH_RESULT_ROUTES.SALE,
      isShow: !isSmall,
      cypress: 'sale',
      onClick: () => {
        dispatch(
          setCurrentState({
            ...storedFormState,
            city_id: Cities.Baku.id,
            leased: 'false',
            category_id: BUILDING_TYPE_ID.FLAT_NEW
          })
        );
        dispatch(setAdriverSelectedCity(Cities.Baku.az));
      }
    },
    {
      title: t('main_menu.lease'),
      path: SEARCH_RESULT_ROUTES.RENT,
      isShow: !isSmall,
      cypress: 'lease',
      onClick: () => {
        dispatch(
          setCurrentState({
            ...storedFormState,
            city_id: Cities.Baku.id,
            leased: 'true',
            category_id: BUILDING_TYPE_ID.FLAT_NEW
          })
        );
        dispatch(setAdriverSelectedCity(Cities.Baku.az));
      }
    },
    {
      title: t('search.form.paid_daily'),
      path: SEARCH_RESULT_ROUTES.RENT_DAILY,
      isShow: !isSmall,
      cypress: 'paid-daily',
      onClick: () => {
        dispatch(
          setCurrentState({
            ...storedFormState,
            city_id: Cities.Baku.id,
            leased: 'true',
            category_id: BUILDING_TYPE_ID.FLAT_NEW,
            paid_daily: ['true']
          })
        );
        dispatch(setAdriverSelectedCity(Cities.Baku.az));
      }
    },
    {
      title: t('main_menu.agencies'),
      path: '/agentlikler',
      isShow: isSmallDesktop || isLarge,
      cypress: 'agencies'
    },
    {
      title: t('main_menu.residences'),
      path: '/yasayis-kompleksleri',
      isShow: isSmallDesktop || isLarge,
      cypress: 'residences'
    }
  ];

  const buttonsGroupList = [
    {
      button: (
        <Tooltip
          text={t('pages.bookmarks.title')}
          wrapperHeight={30}
          wrapperWidth={30}
          tooltipTopSpace={45}
        >
          <BookmarkIconLink
            data-cy="favorites-icon-btn"
            href="/bookmarks"
            data-stat="header-btn"
            aria-label={t('pages.bookmarks.title')}
          />
        </Tooltip>
      ),
      isShow: !isSmall
    },
    {
      button: (
        <CreateItemIconLink
          href="/items/new"
          data-cy="header-add-new-item-btn"
          data-stat="header-add-new-item-btn"
        >
          <WhitePlusInCircle />
          {t('common.create_new_item')}
        </CreateItemIconLink>
      ),
      isShow: isLarge
    },
    {
      button: (
        <LinkWithIcon
          cypress="add-new-icon-btn"
          href="/items/new"
          stat="header-btn"
          icon={Plus}
          width={30}
          height={30}
          indent={false}
          aria-label={t('common.create_new_item')}
        />
      ),
      isShow: isMedium
    },
    {
      button: (
        <i
          data-cy="add-new-icon-btn"
          data-stat="header-btn"
          onClick={onMobileIconPlusClick}
          onKeyPress={onMobileIconPlusClick}
          role="button"
          tabIndex="-1"
          aria-label={t('common.create_new_item')}
        >
          <Icon indent={false} IconComponent={Plus} width={30} height={30} />
        </i>
      ),
      isShow: isSmall && !isWebView
    },
    {
      button: (
        <LoginButton
          {...{ isAuth, isLoadingProfile }}
          profileData={data?.profile}
        />
      ),
      isShow: !isSmall
    },
    {
      button: (
        <WebViewButton onClick={closeWebview}>
          <i>
            <Icon
              IconComponent={CloseWebview}
              width={15}
              height={15}
              indent={false}
              color={'#a9a39e'}
            />
          </i>
        </WebViewButton>
      ),
      isShow: isSmall && isWebView && isShowCloseButton
    }
  ];

  return (
    <FlexedHeader
      data-cy="header"
      id="header"
      data-lotriver-header
      {...{ isFixedHeader }}
    >
      <Wrapper>
        <HamburgerMenu />
        <MenuItemsContainer data-cy="header-items">
          {menuItemsList.map(({ title, path, isShow, cypress, onClick }) => {
            if (!isShow) return null;

            const isCurrentPage = getIsCurrentPage(path, router);

            return (
              <MenuItem
                href={path}
                key={title}
                data-cy={cypress}
                onClick={onClick}
                {...{ isCurrentPage }}
              >
                {title}
              </MenuItem>
            );
          })}
        </MenuItemsContainer>
        <ButtonsGroupContainer>
          {buttonsGroupList.map(({ button, isShow }, index) => {
            if (!isShow) return null;

            return <React.Fragment key={index}>{button}</React.Fragment>;
          })}
        </ButtonsGroupContainer>
      </Wrapper>
    </FlexedHeader>
  );
};
